import {useAlert} from "@contexts/hooks/useAlert.ts";
import {ISolicitacaoItem} from "@models/solicitacoes/solicitacoes";
import {ExpandMore, LinkOutlined} from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Badge,
	Button,
	Grid,
	IconButton,
	Menu,
	MenuItem,
	SxProps,
	TableCell,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {OrcamentosPage} from "@pages/pendencias/Orcamentos/OrcamentosPage.tsx";
import {ToDateDefault, ToMoney} from "@shared/MathHelp.ts";
import {statusAprovacaoColor} from "@models/solicitacoes/orcamento.ts";
import {DefaultInformationProps, DefaultNavLinkProps, DefaultNavTextFieldProps} from "@models/DefaultProps.ts";
import React, {useEffect, useState} from "react";
import {NavigationLink} from "@shared/custom/NavigationLink.tsx";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {TextFieldNav} from "@shared/custom/TextFieldNav.tsx";
import {_Sitemap} from "@localization/siteMap/breadcrumbs/_Sitemap.ts";
import {MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import {UsuarioInformacao} from "@components/contas/UsuarioInformacao.tsx";
import {__usuarios_endpoints} from "@components/usuario/endpoints/__usuarios_endpoints.ts";
import {useTheme} from "@mui/joy";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import {ISolicitacaoItemValor} from "@components/solicitacoes/dto/models/ISolicitacaoItemValor.ts";
import {SolicitacaoReferenciasPage} from "@components/solicitacoes/pages/referencias/SolicitacaoReferenciasPage.tsx";

interface SolicitacaoItemInformacaoProps extends DefaultInformationProps<ISolicitacaoItem> {
	variant: 'grid' | 'navigationLink' | 'row' | 'accordion' | 'list';
	EndComponent?: (item: ISolicitacaoItem) => React.ReactNode;
}

export const SolicitacaoItemInformacao: React.FC<SolicitacaoItemInformacaoProps> = (props) => {
	const {variant} = props;

	if (variant == 'grid') return <SolicitacaoItemInformacaoGrid {...props} />;
	if (variant == 'navigationLink') return <SolicitacaoItemInformacaoNavLink {...props}/>;
	if (variant == 'row') return <SolicitacaoItemInformacaoRow {...props}
	                                                           value={props.value as unknown as ISolicitacaoItemValor}/>;
	if (variant == 'accordion') return <SolicitacaoItemInformacaoAccordion {...props}/>;
	if (variant == 'list') return <SolicitacaoItemInformacaoList {...props}/>;
};

export const SolicitacaoItemInformacaoList: React.FC<Omit<SolicitacaoItemInformacaoProps, 'variant' | 'overrides'>> = (props) => {
	const {value, EndComponent} = props;

	return (
		<Grid container justifyContent={'space-between'} alignItems={'center'}>
			<Grid size={'grow'}>
				<Grid container spacing={1}>
					<Grid>{value.id}</Grid>
					<Grid><SolicitacaoItemInformacaoNavLink slotProps={{chip: {tableMode: true}}} {...props}/></Grid>
					<Grid>{value.quantidade}un(s)</Grid>
					<Grid>est.:{ToMoney(value.valorEstimado)}</Grid>
				</Grid>
			</Grid>
			<Grid>
				{EndComponent && <EndComponent {...value}/>}
			</Grid>
		</Grid>);
};

export const SolicitacaoItemInformacaoTextField: React.FC<Omit<SolicitacaoItemInformacaoProps, 'variant' | 'overrides'> & DefaultNavTextFieldProps> = (props) => {
	const {value, label} = props;

	return (
		<TextFieldNav label={label ?? value.produto?.nome}
		              displayValue={value.nome}
		              slotProps={{textField: props.slotProps?.textField}}
		              onClick={props.onClick}
		              displayMode={'nome'}
		              pathName={_Sitemap.produtos.children.detalhes.fullUrl}
		              searchData={{produtoId: value.produto?.id}}/>
	);
}

export const SolicitacaoItemInformacaoGrid: React.FC<Omit<SolicitacaoItemInformacaoProps, 'overrides' | 'variant' | 'EndComponent'> & {
	sx?: SxProps
}> = (props) => {
	const {value} = props;
	const {Get} = useApi()
	const {adjustSizeLevel} = useGeneral();
	const {userData} = useAuth()
	const [solicitante, setSolicitante] = useState(value.solicitante);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	const theme = useTheme();
	const colorName = statusAprovacaoColor(value.status)
	// @ts-ignore
	const color = theme.palette[colorName] || theme.palette.primary;
	const colorHex = color.main || color['500'] || color['600'] || color['700'] || '#000';

	useEffect(() => {
		Get(__usuarios_endpoints.GetContas({ids: [value.solicitanteId]}, {page: 1, pageSize: 1})).then((x) => {
			if (x.hasData) {
				setSolicitante(x.items[0]);
			}
		})
	}, [solicitante == null]);

	return (
		<Grid size={12} sx={{...props.sx}}>
			<Grid container justifyContent={'space-between'}>
				<Grid size={adjustSizeLevel({
					md: 6,
					sm: 12
				})}>
					<TextField value={value.quantidade}
					           label={'Quantidade solicitada'} disabled/>
				</Grid>
				<Grid size={adjustSizeLevel({
					md: 6,
					sm: 12
				})}>
					<SolicitacaoItemInformacaoTextField {...props}/>
				</Grid>
				<Grid size={adjustSizeLevel({
					md: 6,
					sm: 12
				})}> <MoneyInputEntry value={value.valorEstimado} label={'Valor estimado unidade (solicitante)'}/>
				</Grid>
				<Grid size={adjustSizeLevel({
					md: 6,
					sm: 12
				})}> <TextField value={value.status}
				                label={'Status'}
				                slotProps={{
					                input: {
						                readOnly: true,
						                sx: {
							                fontWeight: 'bold',
						                },
						                endAdornment: <IconButton

							                id="status-button"
							                aria-controls={open ? 'status-menu' : undefined}
							                aria-haspopup="true"
							                aria-expanded={open ? 'true' : undefined}
							                onClick={handleClick}
						                >
							                <ExpandMore/>
						                </IconButton>
					                },
					                htmlInput: {
						                sx: {
							                color: colorHex
						                }
					                }
				                }}
					//@ts-ignore
					            color={statusAprovacaoColor(value.status)}/>
					<Menu id="status-menu"
					      anchorEl={anchorEl}
					      open={open}
					      onClose={handleClose}>
						<MenuItem onClick={() => {
							handleClose()
						}}
						          disabled={value.solicitanteId != userData?.id}>Cancelar</MenuItem>
						<MenuItem onClick={() => {
							handleClose();
						}}
						          disabled={value.solicitanteId != userData?.id}>Finalizar</MenuItem>
					</Menu>
				</Grid>
				<Grid size={adjustSizeLevel({
					md: 6,
					sm: 12
				})} mt={1.5}>
					<UsuarioInformacao variant={'textfield'}
					                   label={'Solicitante'}
					                   value={solicitante}
					                   usuarioId={value.solicitanteId}/>
				</Grid>
				<Grid></Grid>
			</Grid>
		</Grid>
	);
};

export function SolicitacaoItemInformacaoRow(props: Omit<SolicitacaoItemInformacaoProps, 'variant' | 'overrides' | 'value'> & {
	value: ISolicitacaoItemValor;
}) {
	const {value, EndComponent} = props;
	const {addPopUp} = useAlert()


	return (
		<TableRow>
			<TableCell>
				<SolicitacaoItemInformacaoNavLink slotProps={{chip: {tableMode: true}}}
				                                  value={value as unknown as ISolicitacaoItem}/>
			</TableCell>
			<TableCell>
				{ToMoney(value.valorEstimado ?? 0)}
			</TableCell>
			<TableCell>
				{value.quantidade}
			</TableCell>
			<TableCell>
				<IconButton onClick={() => {
					addPopUp({
						title: "Referencias",
						content: <SolicitacaoReferenciasPage hideHeader filtersProps={{
							filters: {idItem: value.id}, setFilters: () => {
							}
						}}/>,
						size: 'lg'
					})
				}}>
					<Badge badgeContent={value.qtdReferencias} color={'primary'}>

						<LinkOutlined/>
					</Badge>

				</IconButton>
			</TableCell>
			<TableCell>
				<Typography color={statusAprovacaoColor(value.status)}>{value.status} </Typography>
			</TableCell>
			<TableCell align={'right'}>
				{EndComponent && <EndComponent {...value as unknown as ISolicitacaoItem}/>}
			</TableCell>
		</TableRow>
	);
}

export const SolicitacaoItemInformacaoNavLink: React.FC<Omit<SolicitacaoItemInformacaoProps, 'EndComponent' | 'variant' | 'overrides'> & DefaultNavLinkProps<ISolicitacaoItem>> = (props) => {
	return <NavigationLink
		searchData={{
			produtoId: props.value.produtoId,
			itemId: props.value.id,
			solicitacaoId: props.value.solicitacaoId,
			...props
		}}
		pathName={'/solicitacao/detalhes'}
		hash={'Orcamentos'}
		toolTipHint={"Ir para a pagina de detalhes do item " + props.value.id}
		onClick={props.onClick}
		chipSx={{...props.slotProps?.chip?.sx}}
		tableMode={props.slotProps?.chip?.tableMode}
		label={props.value.produto?.nome || props.value.produtoId.toString()}
		{...props}

	/>;
};

export const SolicitacaoItemInformacaoAccordion: React.FC<Omit<SolicitacaoItemInformacaoProps, 'EndComponent' | 'variant' | 'overrides'>> = (props) => {
	const {value} = props;
	const {addPopUp} = useAlert();

	return (<Accordion>
		<AccordionSummary expandIcon={
			<ExpandMore/>}><Typography
			variant={'subtitle1'}>{value.produto?.id} {value.produto?.nome} {value.quantidade}un</Typography>
		</AccordionSummary>
		<AccordionDetails>
			<Grid container spacing={1}>
				<Grid size={12}>
					<Typography> {value.produto?.nome}</Typography>
				</Grid>
				<Grid size={12}>
					<Typography color={statusAprovacaoColor(value.status)}> {value.status}</Typography>
				</Grid>

				<Grid size={12}>
					Quantidade: {value.quantidade}
				</Grid>
				<Grid size={12}>
					Valor estimado: {ToMoney(value.valorEstimado)}
				</Grid>
				<Grid size={12}>
					Data de inclusão: {ToDateDefault(value.dataInclusao)}
				</Grid>
				<Grid size={12}>
					Observação: {value.observacao}
				</Grid>
				<Button onClick={() => {
					addPopUp({
						title: 'Orçamentos do item ' + value.produto?.nome + ' na solicitação ' + value.solicitacaoId,
						content: <OrcamentosPage overrides={{titulo: '', size: 'large'}}
						                         filtros={{
							                         idItem: [value.produtoId],
							                         idSolicitacao: [value.solicitacaoId],
						                         }}
						/>, size: 'xl'
					});
				}}>Orçamentos</Button>
			</Grid>
		</AccordionDetails>
	</Accordion>);
};
